import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useAddresses } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, MapPin, Plus, Trash2, ChevronRight, LogOut, Settings, HelpCircle, Gift, Copy, Share2, Shield, AlertTriangle
} from 'lucide-react';
import AddressPickerModal from '../../components/common/AddressPickerModal';
import ProfilePhotoUpload from '../../components/common/ProfilePhotoUpload';
import CustomerBottomNav from '../../components/common/CustomerBottomNav';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function CustomerProfile() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { addresses, addAddress, deleteAddress, isLoading } = useAddresses();
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [stats, setStats] = useState({ bookings: 0, wallet: 0 });
  
  // Handle adding address from the new modal
  const handleSaveAddress = async (addressData) => {
    try {
      await addAddress(addressData);
      toast.success('Address saved!');
      setShowAddressDialog(false);
    } catch (error) {
      toast.error('Failed to save address');
    }
  };
  
  useEffect(() => {
    fetchReferralData();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [bookingsRes, walletRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/wallet`, { headers }).catch(() => ({ data: { balance: 0 } }))
      ]);
      setStats({
        bookings: Array.isArray(bookingsRes.data) ? bookingsRes.data.length : 0,
        wallet: walletRes.data?.balance || 0
      });
    } catch (e) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchReferralData = async () => {
    try {
      const [codeRes, statsRes] = await Promise.all([
        axios.get(`${API}/referral/my-code`),
        axios.get(`${API}/referral/stats`)
      ]);
      setReferralData({ code: codeRes.data, stats: statsRes.data });
    } catch (error) {
      console.error('Failed to fetch referral data');
    }
  };

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    setApplyingCode(true);
    try {
      await axios.post(`${API}/referral/apply`, { referral_code: referralCode });
      toast.success('Referral applied! ₱100 added to your wallet!');
      setReferralCode('');
      fetchReferralData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid referral code');
    } finally {
      setApplyingCode(false);
    }
  };

  const copyReferralCode = () => {
    if (referralData?.code?.code) {
      navigator.clipboard.writeText(referralData.code.code);
      toast.success('Referral code copied!');
    }
  };

  const shareReferral = async () => {
    if (referralData?.code?.code) {
      const shareData = {
        title: 'Join HandyBayani!',
        text: `Use my referral code ${referralData.code.code} to get ₱100 off your first booking!`,
        url: window.location.origin
      };
      try {
        if (navigator.share) {
          await navigator.share(shareData);
        } else {
          navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
          toast.success('Share link copied!');
        }
      } catch (e) {
        console.log('Share failed');
      }
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await deleteAddress(addressId);
      toast.success('Address deleted');
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-slate-900 pt-4 pb-12 px-4 rounded-b-3xl">
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate('/home')}
            className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white font-[Poppins]">Profile</h1>
        </div>
        
        {/* Profile Card */}
        <div className="flex items-center gap-4">
          <ProfilePhotoUpload
            currentImage={user?.profile_image}
            userName={user?.name}
            onImageUpdate={() => refreshUser?.()}
            size="md"
          />
          <div>
            <h2 className="text-xl font-bold text-white">{user?.name || 'User'}</h2>
            <p className="text-slate-400">{user?.email || user?.phone}</p>
          </div>
        </div>
      </header>

      <main className="px-4 -mt-6">
        {/* Quick Stats */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-6" data-testid="customer-stats">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="total-bookings">{stats.bookings}</p>
              <p className="text-xs text-slate-500">Bookings</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600" data-testid="wallet-balance">Php {stats.wallet.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Wallet</p>
            </div>
          </div>
        </div>

        {/* Referral Section */}
        <section className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900 font-[Poppins]">Refer & Earn</h3>
          </div>
          
          <div className="bg-gradient-to-r from-amber-500 to-amber-400 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-bold">Earn ₱100 per referral!</p>
                <p className="text-amber-100 text-sm">Your friend gets ₱100 too</p>
              </div>
            </div>
            
            {referralData?.code?.code && (
              <div className="bg-white/20 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-xs">Your referral code</p>
                  <p className="text-white font-bold text-lg">{referralData.code.code}</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-white hover:bg-white/20"
                    onClick={copyReferralCode}
                    data-testid="copy-referral-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-white hover:bg-white/20"
                    onClick={shareReferral}
                    data-testid="share-referral-btn"
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            
            <div className="flex gap-4 mt-3 text-center">
              <div className="flex-1 bg-white/10 rounded-lg p-2">
                <p className="text-2xl font-bold text-white">{referralData?.stats?.total_referrals || 0}</p>
                <p className="text-xs text-amber-100">Referrals</p>
              </div>
              <div className="flex-1 bg-white/10 rounded-lg p-2">
                <p className="text-2xl font-bold text-white">₱{referralData?.stats?.total_earnings || 0}</p>
                <p className="text-xs text-amber-100">Earned</p>
              </div>
            </div>
            
            <div className="flex gap-2 mt-3">
              <Button 
                className="flex-1 bg-white text-amber-600 hover:bg-amber-50"
                onClick={() => setShowReferralDialog(true)}
                data-testid="apply-referral-btn"
              >
                Have a code? Apply
              </Button>
              <Button 
                variant="outline"
                className="flex-1 bg-white/10 border-white/30 text-white hover:bg-white/20"
                onClick={() => navigate('/referral')}
                data-testid="view-referral-btn"
              >
                View Details
              </Button>
            </div>
          </div>
        </section>

        {/* Saved Addresses */}
        <section className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900 font-[Poppins]">Saved Addresses</h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowAddressDialog(true)}
              data-testid="add-address-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
          
          <div className="space-y-3">
            {isLoading ? (
              <div className="animate-pulse bg-stone-200 rounded-xl h-20" />
            ) : addresses.length > 0 ? (
              addresses.map(address => (
                <div 
                  key={address.id}
                  className="bg-white rounded-xl p-4 shadow-sm flex items-start justify-between"
                >
                  <div className="flex gap-3">
                    <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{address.label}</p>
                      <p className="text-sm text-slate-500">{address.full_address}</p>
                      <p className="text-xs text-slate-400">{address.city}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleDeleteAddress(address.id)}
                    className="text-red-500 hover:text-red-600"
                    data-testid={`delete-address-${address.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-6 bg-stone-50 rounded-xl">
                <MapPin className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No addresses saved yet</p>
              </div>
            )}
          </div>
        </section>

        {/* Menu Items */}
        <section className="mb-24">
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {/* Verification Banner - Show if not verified */}
            {!user?.identity_verified && (
              <button 
                className="w-full p-4 flex items-center justify-between bg-amber-50 hover:bg-amber-100 border-b border-amber-200"
                onClick={() => navigate('/verification')}
                data-testid="verification-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="text-left">
                    <span className="font-medium text-amber-900 block">Verify Your Identity</span>
                    <span className="text-xs text-amber-700">Required to book services</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-600" />
              </button>
            )}
            
            {/* Show verified badge if verified */}
            {user?.identity_verified && (
              <div className="p-4 flex items-center gap-3 bg-green-50 border-b border-green-200">
                <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <span className="font-medium text-green-900 block">Identity Verified</span>
                  <span className="text-xs text-green-700">Your account is verified</span>
                </div>
              </div>
            )}
            
            <button 
              className="w-full p-4 flex items-center justify-between hover:bg-stone-50"
              onClick={() => navigate('/profile/settings')}
              data-testid="account-settings-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Settings className="w-5 h-5 text-blue-600" />
                </div>
                <span className="font-medium text-slate-900">Account Settings</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
            
            <button 
              className="w-full p-4 flex items-center justify-between hover:bg-stone-50 border-t border-stone-100"
              onClick={() => navigate('/disputes')}
              data-testid="help-support-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-green-600" />
                </div>
                <span className="font-medium text-slate-900">Help & Support</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
            
            <button 
              className="w-full p-4 flex items-center justify-between hover:bg-stone-50 border-t border-stone-100"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <LogOut className="w-5 h-5 text-red-600" />
                </div>
                <span className="font-medium text-red-600">Log Out</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </section>
      </main>

      {/* Bottom Navigation */}
      <CustomerBottomNav />

      {/* Add Address Dialog */}
      <AddressPickerModal
        open={showAddressDialog}
        onClose={() => setShowAddressDialog(false)}
        onSave={handleSaveAddress}
      />

      {/* Referral Code Dialog */}
      <Dialog open={showReferralDialog} onOpenChange={setShowReferralDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Apply Referral Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 rounded-xl p-4 text-center">
              <Gift className="w-12 h-12 text-amber-500 mx-auto mb-2" />
              <p className="text-amber-800 font-medium">Get ₱100 credit!</p>
              <p className="text-sm text-amber-600">Enter a friend's referral code</p>
            </div>
            <div>
              <Label>Referral Code</Label>
              <Input
                placeholder="e.g., HBJOH1A2B3"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                className="h-12 mt-1.5 text-center text-lg font-bold tracking-widest"
                data-testid="referral-code-input"
              />
            </div>
            <Button 
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900"
              onClick={handleApplyReferral}
              disabled={applyingCode}
              data-testid="submit-referral-btn"
            >
              {applyingCode ? 'Applying...' : 'Apply Code'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
